/**
 * Platform Admin — вход под конкретным пользователем компании (AK-470).
 *
 * Backend contract — `backend/src/ikolhoz/api/v1/platform.py`:
 *   GET  /api/v1/platform/companies/{tenant_id}/members        → MemberRow[]
 *   GET  /api/v1/platform/users?has_platform_role=true         → PlatformUserRow[]
 *   POST /api/v1/platform/companies/{tid}/users/{uid}/impersonate
 *        body { step_up_token? } → { user_id, full_name, tenant_id,
 *                                    tenant_name, mode, expires_at }
 *
 * Режим (`read` / `write`) считает сервер по роли вошедшего админа —
 * клиент его не выбирает и не угадывает.
 *
 * Почему платформенных сотрудников тянем отдельным запросом: `MemberRow` не
 * отдаёт `platform_role`, а кнопку «Войти под ним» у них надо гасить (сервер
 * такой вызов отобьёт 403, но узнавать об этом по ошибке — плохой UX).
 * Список платформенных сотрудников короткий, запрашивается только когда
 * строка компании реально раскрыта.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';

// ─── Типы (mirror Pydantic) ───────────────────────────────────────────────

/** `platform.MemberRow` — участник компании. */
export interface TenantMember {
  member_id: string;
  user_id: string;
  email: string;
  display_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  role: string;
  cluster_id?: string | null;
  cluster_name?: string | null;
  farm_id?: string | null;
  farm_name?: string | null;
  is_active: boolean;
  /**
   * `active` / `blocked` / `archived`. `is_active` выводится из него
   * триггером и отвечает только «пускать ли» — заблокированный и архивный
   * с одним `false` неразличимы, а вернуть доступ можно только первому.
   */
  status?: string | null;
  invited_at?: string | null;
  accepted_at?: string | null;
  last_login_at?: string | null;
}

/** `platform.PlatformUserRow` — нужен только id + platform_role. */
interface PlatformUserRow {
  id: string;
  platform_role?: string | null;
}

export interface ImpersonateUserResult {
  user_id: string;
  full_name: string;
  tenant_id: string;
  tenant_name: string;
  mode: 'read' | 'write';
  expires_at: string;
}

export interface ImpersonateUserPayload {
  tenantId: string;
  userId: string;
  stepUpToken?: string;
}

// ─── Query keys ───────────────────────────────────────────────────────────

export const impersonationKeys = {
  members: (tenantId: string) =>
    ['platform-admin', 'company-members', tenantId] as const,
  platformStaff: () => ['platform-admin', 'platform-staff'] as const,
};

// ─── Queries ──────────────────────────────────────────────────────────────

/** Участники компании. `enabled` — строка компании раскрыта. */
export function useTenantMembers(
  tenantId: string | null,
  options?: { enabled?: boolean },
): UseQueryResult<TenantMember[], Error> {
  return useQuery({
    queryKey: impersonationKeys.members(tenantId ?? ''),
    queryFn: () =>
      api.get<TenantMember[]>(
        `/api/v1/platform/companies/${tenantId}/members`,
      ),
    enabled: !!tenantId && (options?.enabled ?? true),
    staleTime: 30_000,
  });
}

/** id сотрудников платформы — под ними вход запрещён. */
export function usePlatformStaffIds(options?: {
  enabled?: boolean;
}): UseQueryResult<Set<string>, Error> {
  return useQuery({
    queryKey: impersonationKeys.platformStaff(),
    queryFn: async () => {
      const rows = await api.get<PlatformUserRow[]>(
        '/api/v1/platform/users?has_platform_role=true&limit=2000',
      );
      return new Set(rows.filter((r) => r.platform_role).map((r) => r.id));
    },
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60_000,
  });
}

// ─── Mutations ────────────────────────────────────────────────────────────

/**
 * Вход под пользователем. Сервер меняет cookie на токен целевого
 * пользователя — после успеха вызывающий обязан почистить кэш и уйти
 * жёстким переходом, иначе на экране останутся данные админа.
 */
export function useImpersonateUser() {
  return useMutation<ImpersonateUserResult, Error, ImpersonateUserPayload>({
    mutationFn: ({ tenantId, userId, stepUpToken }) =>
      api.post<ImpersonateUserResult>(
        `/api/v1/platform/companies/${tenantId}/users/${userId}/impersonate`,
        stepUpToken ? { step_up_token: stepUpToken } : {},
      ),
  });
}

/**
 * Нужен ли код из приложения — решает сервер, а не клиент.
 *
 * Признак берём из ответа: на проде вызов без подтверждения возвращает 403
 * «Требуется подтверждение кодом из приложения», на staging тот же вызов
 * проходит сразу. Клиент нигде не спрашивает «а мы на проде?» — иначе
 * условие пришлось бы держать в двух местах и оно бы разъехалось.
 *
 * Второй 403 на этом эндпоинте — «Под платформенным администратором вход
 * запрещён»; он отличается текстом и кодом не лечится.
 */
export function isStepUpRequired(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    err.status === 403 &&
    /код/i.test(err.detail ?? '')
  );
}

/**
 * Закрыть/вернуть доступ участнику компании со стороны платформы.
 *
 * Бэкенд пишет `status`, а не `is_active`: последний выводится триггером
 * (миграция 064), и прямая запись в него была бы тихим no-op.
 */
export function useSetMemberAccess(): UseMutationResult<
  void,
  Error,
  { tenantId: string; memberId: string; blocked: boolean; reason?: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, memberId, blocked, reason }) =>
      api.post<void>(
        `/api/v1/platform/companies/${tenantId}/members/${memberId}/${blocked ? 'ban' : 'unban'}`,
        blocked ? { reason: reason ?? null } : {},
      ),
    onSuccess: (_r, { tenantId }) => {
      void qc.invalidateQueries({ queryKey: impersonationKeys.members(tenantId) });
    },
  });
}
